/**
 * UUID v4 helpers — a leaf module (no imports) shared by the message outbox,
 * the realtime navigation layer and the send helpers in `services/erpat.ts`.
 *
 * The server's `client_event_id` (message idempotency, phase6-contract.md
 * §3.2.3 / §3.4) and the gateway's `event_id` are both lower-case UUID v4;
 * this is the one place that knows the shape.
 */

/** Lower- or upper-case UUID v4 (version nibble `4`, variant `8`/`9`/`a`/`b`). */
export const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidV4(value: unknown): value is string {
  return typeof value === 'string' && UUID_V4_RE.test(value);
}

/**
 * Mint a lower-case UUID v4. Prefers `crypto.randomUUID()`; falls back to
 * `crypto.getRandomValues()`. There is deliberately NO non-crypto fallback:
 * an idempotency key that could collide would let one message overwrite the
 * server's memory of another, so an environment without Web Crypto throws
 * instead of sending a weak key.
 */
export function newUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().toLowerCase();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (
      `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-` +
      `${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
    );
  }
  throw new Error('[uuid] Web Crypto is unavailable; cannot mint a UUID v4.');
}
